import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';

const ListadoClientesSuplidores = ({ onSeleccionarCliente }) => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;
  const theme = useTheme();
  
  const [tipoFiltro, setTipoFiltro] = useState('Todos');
  const [busqueda, setBusqueda] = useState('');
  const [clientesSuplidores, setClientesSuplidores] = useState([]);
  const [clientesSuplidoresFiltrados, setClientesSuplidoresFiltrados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoVer, setDialogoVer] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina] = useState(10);

  useEffect(() => {
    cargarDatos();
  }, [selectedCompany]);

  useEffect(() => {
    filtrarDatos();
  }, [busqueda, tipoFiltro, clientesSuplidores]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const responseClientes = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: 'Cliente',
        descripcion: '',
      });

      const responseSuplidores = await post('/api/clientesbackend_buscar', {
        id_empresa: selectedCompany,
        tipo: 'Suplidor',
        descripcion: '',
      });

      let todosLosDatos = [];

      if (responseClientes.ok) {
        todosLosDatos = [...todosLosDatos, ...(responseClientes.data.resultados || [])];
      }

      if (responseSuplidores.ok) {
        todosLosDatos = [...todosLosDatos, ...(responseSuplidores.data.resultados || [])];
      }

      setClientesSuplidores(todosLosDatos);
    } catch (error) {
      showError('Error de conexión al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  const filtrarDatos = () => {
    let datosFiltrados = [...clientesSuplidores];

    // Filtrar por tipo
    if (tipoFiltro !== 'Todos') {
      datosFiltrados = datosFiltrados.filter(
        item => item.tiposuplidor === tipoFiltro
      );
    }

    // Filtrar por búsqueda
    if (busqueda.trim()) {
      const busquedaLower = busqueda.toLowerCase();
      datosFiltrados = datosFiltrados.filter(item =>
        item.descrip?.toLowerCase().includes(busquedaLower) ||
        item.rnc?.includes(busqueda) ||
        item.ciudad?.toLowerCase().includes(busquedaLower) ||
        item.email?.toLowerCase().includes(busquedaLower)
      );
    }

    setClientesSuplidoresFiltrados(datosFiltrados);
    setPagina(0);
  };

  const handleVerDetalles = async (id) => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (ok) {
        setClienteSeleccionado(data.cliente);
        setDialogoVer(true);
      } else {
        showError('Error al obtener los detalles');
      }
    } catch (error) {
      showError('Error de conexión');
    }
  };

  const handleEditar = (id) => {
    if (onSeleccionarCliente) {
      onSeleccionarCliente(id);
    }
  };

  const handleEliminar = async (id, tipo) => {
    if (!window.confirm('¿Está seguro que desea eliminar este registro?')) {
      return;
    }

    try {
      const responseGet = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (responseGet.ok) {
        const rnc = responseGet.data.cliente.rnc;

        const { ok, data } = await post('/api/clientesbackend_eliminar', {
          id_empresa: selectedCompany,
          rnc: rnc,
          tipo: tipo,
        });

        if (ok) {
          showSuccess('Registro eliminado exitosamente');
          cargarDatos();
        } else {
          showError(data.Error || 'Error al eliminar');
        }
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setTipoFiltro('Todos');
  };

  // Paginación
  const inicio = pagina * filasPorPagina;
  const fin = inicio + filasPorPagina;
  const datosPaginados = clientesSuplidoresFiltrados.slice(inicio, fin);
  const totalPaginas = Math.ceil(clientesSuplidoresFiltrados.length / filasPorPagina);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Listado de Clientes y Suplidores
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={limpiarFiltros}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Limpiar
          </Button>
          <Button
            variant="contained"
            onClick={cargarDatos}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Buscar"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre, RNC, Ciudad, Email..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Filtrar por Tipo</InputLabel>
                <Select
                  value={tipoFiltro}
                  onChange={(e) => setTipoFiltro(e.target.value)}
                  label="Filtrar por Tipo"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  <MenuItem value="Cliente">Clientes</MenuItem>
                  <MenuItem value="Suplidor">Suplidores</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Chip
                  label={`Total: ${clientesSuplidoresFiltrados.length} registros`}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>RNC</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ciudad</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Teléfono</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cargando ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography>Cargando...</Typography>
                  </TableCell>
                </TableRow>
              ) : datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No se encontraron registros
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        icon={
                          item.tiposuplidor === 'Cliente' ? (
                            <PersonIcon />
                          ) : (
                            <BusinessIcon />
                          )
                        }
                        label={item.tiposuplidor}
                        color={item.tiposuplidor === 'Cliente' ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.descrip}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.rnc}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {item.ciudad}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.telefono ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {item.telefono}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {item.email}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Ver detalles">
                          <IconButton
                            size="small"
                            onClick={() => handleVerDetalles(item.id)}
                            sx={{
                              color: theme.palette.info.main,
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditar(item.id)}
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleEliminar(item.id, item.tiposuplidor)}
                            sx={{
                              color: theme.palette.error.main,
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {totalPaginas > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Mostrando {inicio + 1} - {Math.min(fin, clientesSuplidoresFiltrados.length)} de{' '}
              {clientesSuplidoresFiltrados.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setPagina(Math.max(0, pagina - 1))}
                disabled={pagina === 0}
                sx={{ textTransform: 'none' }}
              >
                Anterior
              </Button>
              <Button
                size="small"
                onClick={() => setPagina(Math.min(totalPaginas - 1, pagina + 1))}
                disabled={pagina >= totalPaginas - 1}
                sx={{ textTransform: 'none' }}
              >
                Siguiente
              </Button>
            </Box>
          </Box>
        )}
      </Card>

      {/* Diálogo de Ver Detalles */}
      <Dialog
        open={dialogoVer}
        onClose={() => setDialogoVer(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Detalles del {clienteSeleccionado?.tiposuplidor}
            </Typography>
            <IconButton
              onClick={() => setDialogoVer(false)}
              sx={{ color: 'white' }}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {clienteSeleccionado && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Nombre
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {clienteSeleccionado.descrip}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  RNC
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {clienteSeleccionado.rnc}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Dirección
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {clienteSeleccionado.direccion || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Ciudad
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {clienteSeleccionado.ciudad || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {clienteSeleccionado.telefono || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  WhatsApp/Celular
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {clienteSeleccionado.fax || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {clienteSeleccionado.email || '-'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDialogoVer(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cerrar
          </Button>
          {clienteSeleccionado && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setDialogoVer(false);
                handleEditar(clienteSeleccionado.id);
              }}
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListadoClientesSuplidores;
